import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import useRefreshToken from "../../hooks/useRefreshToken/useRefreshToken";
import { logout } from "../../authSlice";
import MainLoader from "../../../../common/components/Loader/Loader";
import useActivityTracker from "../../hooks/useActivityTracker/useActivityTracker";


const PersistLogin = () => {
  const dispatch = useDispatch();
  const refresh = useRefreshToken();
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, accessToken } = useSelector((state) => state.auth);
  const lastActiveRef = useRef(Date.now());

  const location = useLocation();
 
  // Track user activity
  useActivityTracker((timestamp) => {
    lastActiveRef.current = timestamp;
  });

  //  Check token once on mount
  useEffect(() => {
    let isMounted = true;

    const isTokenExpired = (token) => {
      try {
        const decoded = jwtDecode(token);
        return decoded.exp * 1000 < Date.now();
      } catch (err) {
        return true; // treat invalid token as expired
      }
    };

    const verifyRefreshToken = async () => {
      try {
        await refresh(); // refresh handles state update
      } catch (err) {
        if (err.response?.status === 401) {
          dispatch(logout());
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      if (!accessToken || isTokenExpired(accessToken)) {
        verifyRefreshToken();
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, accessToken, dispatch, refresh]);

  //  Background refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      const lastActive = lastActiveRef.current;
      const isUserActive = now - lastActive < 1 * 3600 * 1000; // active in last 1 hour
      
      if (isUserActive) {
        try {
          await refresh(); // already updates Redux state
          // console.log(" Background token refreshed");
        } catch (err) {
          // console.error(" Refresh failed", err);
          dispatch(logout());
        }
      }
       else {
        // console.warn("User inactive > 1 hour → logging out");
        dispatch(logout());
      }
    }, (1 * 60 + 50) * 60 * 1000); // check every 1 hour 50 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, accessToken, dispatch, refresh]);

  //  Show loader only during first-time check
  if (isLoading) {
    return <MainLoader />;
  }


  return isAuthenticated ? <Outlet /> : <Navigate to="/" state={{ from: location }} replace />;
};

export default PersistLogin;



